import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { fetchData, getAgents } from "../../../api/client";

const DEFAULT_ANNOUNCE =
  "Por favor, aguarde. Vamos transferir você para um de nossos atendentes.";

export const EMPTY_VOICE_FORM = {
  messageMode: "tts",
  audioUrl: "",
  afterAction: "hangup",
  chatbotId: "",
  agentId: "",
  humanMode: "channel_queue",
  userId: "",
  transferAnnounceEnabled: true,
  transferAnnounceText: DEFAULT_ANNOUNCE,
  retryMode: "none",
  retryMaxAttempts: 3,
  retryIntervalMinutes: 60,
};

function isUraUser(user) {
  const r = user?.ura;
  return r === 1 || r === "1" || r === true;
}

async function uploadCampaignAudio(file) {
  const sign = await fetchData("/uploads/sign", "POST", {
    filename: file.name,
    contentType: file.type || "audio/mpeg",
    prefix: "campaigns/voice",
  });
  const uploadUrl = sign?.data?.uploadUrl;
  const fileUrl = sign?.data?.fileUrl;
  if (!uploadUrl || !fileUrl) throw new Error("Falha ao assinar upload do áudio.");
  const put = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type || "audio/mpeg" },
    body: file,
  });
  if (!put.ok) throw new Error(`Falha no upload do áudio (${put.status}).`);
  return fileUrl;
}

export function validateVoiceForm(form, voiceFields) {
  if (!voiceFields) return null;
  if (voiceFields.messageMode === "audio" && !String(voiceFields.audioUrl || "").trim()) {
    return "Envie o áudio que será reproduzido na ligação.";
  }
  if (voiceFields.messageMode === "tts" && !String(form?.text || "").trim()) {
    return "Informe o texto que será lido na ligação.";
  }
  if (voiceFields.afterAction === "chatbot" && !voiceFields.chatbotId) {
    return "Selecione o chatbot para continuar após a mensagem.";
  }
  if (voiceFields.afterAction === "agent" && !voiceFields.agentId) {
    return "Selecione o agente de IA para continuar após a mensagem.";
  }
  if (
    voiceFields.afterAction === "human" &&
    voiceFields.humanMode === "user" &&
    !voiceFields.userId
  ) {
    return "Selecione o atendente humano para a transferência.";
  }
  if (voiceFields.retryMode === "fixed") {
    const n = Number(voiceFields.retryMaxAttempts);
    if (!Number.isFinite(n) || n < 1 || n > 50) {
      return "Tentativas devem ser entre 1 e 50.";
    }
  }
  const interval = Number(voiceFields.retryIntervalMinutes);
  if (
    voiceFields.retryMode !== "none" &&
    (!Number.isFinite(interval) || interval < 1)
  ) {
    return "Informe o intervalo entre tentativas (mínimo 1 minuto).";
  }
  return null;
}

export function buildVoiceContentPayload(form, voiceFields) {
  const afterTarget = {};
  if (voiceFields.afterAction === "chatbot") {
    afterTarget.chatbotId = Number(voiceFields.chatbotId) || undefined;
  } else if (voiceFields.afterAction === "agent") {
    afterTarget.agentId = Number(voiceFields.agentId) || undefined;
  } else if (voiceFields.afterAction === "human") {
    afterTarget.queueMode = voiceFields.humanMode === "user" ? "user" : "channel_queue";
    if (voiceFields.humanMode === "user") {
      afterTarget.userId = Number(voiceFields.userId) || undefined;
    }
  }

  const retry =
    voiceFields.retryMode === "none"
      ? { mode: "none", maxAttempts: 1, intervalMinutes: Number(voiceFields.retryIntervalMinutes) || 60 }
      : voiceFields.retryMode === "until_answer"
        ? {
            mode: "until_answer",
            maxAttempts: 50,
            intervalMinutes: Number(voiceFields.retryIntervalMinutes) || 60,
          }
        : {
            mode: "fixed",
            maxAttempts: Math.min(50, Math.max(1, Number(voiceFields.retryMaxAttempts) || 1)),
            intervalMinutes: Number(voiceFields.retryIntervalMinutes) || 60,
          };

  return {
    text: voiceFields.messageMode === "tts" ? form.text : undefined,
    voice: {
      messageMode: voiceFields.messageMode,
      audioUrl: voiceFields.messageMode === "audio" ? voiceFields.audioUrl : null,
      ttsText: voiceFields.messageMode === "tts" ? form.text : null,
      afterAction: voiceFields.afterAction,
      afterTarget,
      transferAnnounce: {
        enabled: voiceFields.afterAction === "human" ? Boolean(voiceFields.transferAnnounceEnabled) : false,
        text: voiceFields.transferAnnounceText || DEFAULT_ANNOUNCE,
      },
      retry,
    },
  };
}

export default function VoiceCampaignMessagePanel({
  form,
  setForm,
  voiceFields,
  setVoiceFields,
}) {
  const [users, setUsers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadError, setUploadError] = useState("");

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetchData("/users?limit=200", "GET").catch(() => null),
      getAgents({ page: 1, limit: 100 }).catch(() => null),
    ]).then(([usersRes, agentsRes]) => {
      if (cancelled) return;
      const userItems =
        usersRes?.data?.items ||
        usersRes?.data?.users ||
        usersRes?.data ||
        [];
      setUsers(Array.isArray(userItems) ? userItems : []);
      const agentItems = agentsRes?.data?.agents || agentsRes?.agents || agentsRes?.data || [];
      setAgents(Array.isArray(agentItems) ? agentItems : []);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const chatbots = useMemo(
    () => (users || []).filter((u) => isUraUser(u) && !u.excluido),
    [users]
  );
  const humans = useMemo(
    () => (users || []).filter((u) => !isUraUser(u) && !u.excluido),
    [users]
  );

  const patchVoice = (patch) => setVoiceFields((prev) => ({ ...prev, ...patch }));

  const onPickAudio = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setUploadBusy(true);
    setUploadError("");
    try {
      const url = await uploadCampaignAudio(file);
      patchVoice({ audioUrl: url, messageMode: "audio" });
    } catch (err) {
      setUploadError(err?.message || "Não foi possível enviar o áudio.");
    } finally {
      setUploadBusy(false);
    }
  };

  return (
    <Stack spacing={1.75}>
      <FormControl>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
          Conteúdo da ligação
        </Typography>
        <RadioGroup
          row
          value={voiceFields.messageMode}
          onChange={(e) => patchVoice({ messageMode: e.target.value })}
        >
          <FormControlLabel value="tts" control={<Radio size="small" />} label="Texto (lido)" />
          <FormControlLabel value="audio" control={<Radio size="small" />} label="Áudio" />
        </RadioGroup>
      </FormControl>

      {voiceFields.messageMode === "tts" ? (
        <TextField
          label="Texto lido na ligação *"
          value={form.text}
          onChange={(e) => setForm((current) => ({ ...current, text: e.target.value }))}
          fullWidth
          multiline
          minRows={4}
          helperText="Será falado com voz em português quando o destinatário atender."
        />
      ) : (
        <Stack spacing={1}>
          <Button variant="outlined" component="label" disabled={uploadBusy}>
            {uploadBusy ? "Enviando áudio…" : "Enviar áudio"}
            <input hidden type="file" accept="audio/*,.mp3,.wav,.ogg,.m4a" onChange={onPickAudio} />
          </Button>
          {voiceFields.audioUrl ? (
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all" }}>
              Áudio: {voiceFields.audioUrl}
            </Typography>
          ) : (
            <Typography variant="caption" color="warning.main">
              Nenhum áudio selecionado.
            </Typography>
          )}
          {uploadError ? <Alert severity="error">{uploadError}</Alert> : null}
        </Stack>
      )}

      <FormControl fullWidth size="small">
        <InputLabel id="voice-after-action">Depois da mensagem</InputLabel>
        <Select
          labelId="voice-after-action"
          label="Depois da mensagem"
          value={voiceFields.afterAction}
          onChange={(e) => patchVoice({ afterAction: e.target.value })}
        >
          <MenuItem value="hangup">Encerrar a ligação</MenuItem>
          <MenuItem value="chatbot">Continuar com chatbot</MenuItem>
          <MenuItem value="agent">Continuar com agente de IA</MenuItem>
          <MenuItem value="human">Transferir para atendente humano</MenuItem>
        </Select>
      </FormControl>

      {voiceFields.afterAction === "chatbot" ? (
        <FormControl fullWidth size="small">
          <InputLabel id="voice-chatbot">Chatbot</InputLabel>
          <Select
            labelId="voice-chatbot"
            label="Chatbot"
            value={voiceFields.chatbotId}
            onChange={(e) => patchVoice({ chatbotId: e.target.value })}
          >
            {chatbots.map((u) => (
              <MenuItem key={u.id} value={String(u.id)}>
                {u.nome || u.name || `Chatbot #${u.id}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : null}

      {voiceFields.afterAction === "agent" ? (
        <FormControl fullWidth size="small">
          <InputLabel id="voice-agent">Agente de IA</InputLabel>
          <Select
            labelId="voice-agent"
            label="Agente de IA"
            value={voiceFields.agentId}
            onChange={(e) => patchVoice({ agentId: e.target.value })}
          >
            {agents.map((a) => (
              <MenuItem key={a.id} value={String(a.id)}>
                {a.nome || a.name || `Agente #${a.id}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : null}

      {voiceFields.afterAction === "human" ? (
        <Stack spacing={1.25}>
          <FormControl fullWidth size="small">
            <InputLabel id="voice-human-mode">Destino humano</InputLabel>
            <Select
              labelId="voice-human-mode"
              label="Destino humano"
              value={voiceFields.humanMode}
              onChange={(e) => patchVoice({ humanMode: e.target.value })}
            >
              <MenuItem value="channel_queue">Fila do canal Telefone Xpro</MenuItem>
              <MenuItem value="user">Atendente específico</MenuItem>
            </Select>
          </FormControl>
          {voiceFields.humanMode === "user" ? (
            <FormControl fullWidth size="small">
              <InputLabel id="voice-user">Atendente</InputLabel>
              <Select
                labelId="voice-user"
                label="Atendente"
                value={voiceFields.userId}
                onChange={(e) => patchVoice({ userId: e.target.value })}
              >
                {humans.map((u) => (
                  <MenuItem key={u.id} value={String(u.id)}>
                    {u.nome || u.name || `Usuário #${u.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(voiceFields.transferAnnounceEnabled)}
                onChange={(e) => patchVoice({ transferAnnounceEnabled: e.target.checked })}
              />
            }
            label="Avisar antes de transferir"
          />
          {voiceFields.transferAnnounceEnabled ? (
            <TextField
              label="Mensagem de aviso da transferência"
              value={voiceFields.transferAnnounceText}
              onChange={(e) => patchVoice({ transferAnnounceText: e.target.value })}
              fullWidth
              multiline
              minRows={2}
            />
          ) : null}
        </Stack>
      ) : null}

      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
          Se ocupado ou não atender
        </Typography>
        <FormControl fullWidth size="small" sx={{ mb: 1.25 }}>
          <InputLabel id="voice-retry-mode">Política</InputLabel>
          <Select
            labelId="voice-retry-mode"
            label="Política"
            value={voiceFields.retryMode}
            onChange={(e) => patchVoice({ retryMode: e.target.value })}
          >
            <MenuItem value="none">Não tentar de novo (Não atendida / não entregue)</MenuItem>
            <MenuItem value="fixed">Tentar novamente N vezes</MenuItem>
            <MenuItem value="until_answer">Até atender (máximo 50)</MenuItem>
          </Select>
        </FormControl>
        {voiceFields.retryMode !== "none" ? (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
            {voiceFields.retryMode === "fixed" ? (
              <TextField
                label="Tentativas"
                type="number"
                size="small"
                value={voiceFields.retryMaxAttempts}
                onChange={(e) => patchVoice({ retryMaxAttempts: e.target.value })}
                inputProps={{ min: 1, max: 50 }}
                sx={{ width: { sm: 140 } }}
              />
            ) : null}
            <TextField
              label="Intervalo (minutos)"
              type="number"
              size="small"
              value={voiceFields.retryIntervalMinutes}
              onChange={(e) => patchVoice({ retryIntervalMinutes: e.target.value })}
              inputProps={{ min: 1 }}
              sx={{ width: { sm: 180 } }}
              helperText={
                voiceFields.retryMode === "until_answer"
                  ? "Limite automático de 50 tentativas."
                  : undefined
              }
            />
          </Stack>
        ) : null}
      </Box>
    </Stack>
  );
}
